import React, { useState } from 'react';
import Input from '../../../components/ui/Input';
import { Checkbox } from '../../../components/ui/Checkbox';
import Button from '../../../components/ui/Button';
import Icon from '../../../components/AppIcon';

const SecuritySetupStep = ({ formData, onUpdate, onNext, onBack }) => {
  const [errors, setErrors] = useState({});
  const [passwordStrength, setPasswordStrength] = useState(0);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const calculatePasswordStrength = (password) => {
    let strength = 0;
    if (password?.length >= 8) strength++;
    if (password?.length >= 12) strength++;
    if (/[a-z]/?.test(password) && /[A-Z]/?.test(password)) strength++;
    if (/\d/?.test(password)) strength++;
    if (/[^a-zA-Z0-9]/?.test(password)) strength++;
    return strength;
  };

  const handlePasswordChange = (value) => {
    onUpdate({ ...formData, password: value });
    setPasswordStrength(calculatePasswordStrength(value));
    if (errors?.password) {
      setErrors({ ...errors, password: '' });
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData?.password) {
      newErrors.password = 'Password is required';
    } else if (formData?.password?.length < 8) {
      newErrors.password = 'Password must be at least 8 characters';
    } else if (!/(?=.*[a-z])(?=.*[A-Z])(?=.*\d)/?.test(formData?.password)) {
      newErrors.password = 'Password must contain uppercase, lowercase, and numbers';
    }

    if (!formData?.confirmPassword) {
      newErrors.confirmPassword = 'Please confirm your password';
    } else if (formData?.password !== formData?.confirmPassword) {
      newErrors.confirmPassword = 'Passwords do not match';
    }

    if (!formData?.securityQuestion) {
      newErrors.securityQuestion = 'Security question is required';
    }

    if (!formData?.securityAnswer?.trim()) {
      newErrors.securityAnswer = 'Security answer is required';
    }

    if (!formData?.enable2FA) {
      newErrors.enable2FA = 'Two-factor authentication is required for account security';
    }

    setErrors(newErrors);
    return Object.keys(newErrors)?.length === 0;
  };

  const handleSubmit = (e) => {
    e?.preventDefault();
    if (validateForm()) {
      onNext();
    }
  };

  const getStrengthColor = () => {
    if (passwordStrength <= 1) return 'var(--color-error)';
    if (passwordStrength <= 3) return 'var(--color-warning)';
    return 'var(--color-success)';
  };

  const getStrengthText = () => {
    if (passwordStrength <= 1) return 'Weak';
    if (passwordStrength <= 3) return 'Medium';
    return 'Strong';
  };

  const securityQuestions = [
    "What was the name of your first pet?",
    "What city were you born in?",
    "What is your mother\'s maiden name?",
    "What was the make of your first car?",
    "What is your favorite book?"
  ];

  return (
    <form onSubmit={handleSubmit} className="space-y-4 md:space-y-5 lg:space-y-6">
      <div className="bg-[var(--color-card)] rounded-lg p-4 md:p-6 lg:p-8 border border-[var(--color-border)]">
        <div className="flex items-start gap-3 mb-4 md:mb-5 lg:mb-6">
          <div className="w-10 h-10 md:w-12 md:h-12 rounded-lg bg-[var(--color-primary)]/10 flex items-center justify-center flex-shrink-0">
            <Icon name="Lock" size={20} color="var(--color-primary)" />
          </div>
          <div>
            <h3 className="text-lg md:text-xl lg:text-2xl font-semibold text-[var(--color-foreground)]">
              Create Secure Password
            </h3>
            <p className="text-sm md:text-base text-[var(--color-muted-foreground)] mt-1">
              Choose a strong password to protect your account
            </p>
          </div>
        </div>

        <div className="space-y-4 md:space-y-5 lg:space-y-6">
          <div className="relative">
            <Input
              label="Password"
              type={showPassword ? 'text' : 'password'}
              placeholder="Create a strong password"
              value={formData?.password || ''}
              onChange={(e) => handlePasswordChange(e?.target?.value)}
              error={errors?.password}
              required
            />
            <button
              type="button"
              onClick={() => setShowPassword(!showPassword)}
              className="absolute right-3 top-9 text-[var(--color-muted-foreground)] hover:text-[var(--color-foreground)]"
            >
              <Icon name={showPassword ? 'EyeOff' : 'Eye'} size={18} />
            </button>

            {formData?.password && (
              <div className="mt-3">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-xs md:text-sm text-[var(--color-muted-foreground)]">
                    Password Strength:
                  </span>
                  <span className="text-xs md:text-sm font-medium" style={{ color: getStrengthColor() }}>
                    {getStrengthText()}
                  </span>
                </div>
                <div className="h-2 bg-[var(--color-muted)] rounded-full overflow-hidden">
                  <div
                    className="h-full transition-all duration-300"
                    style={{
                      width: `${(passwordStrength / 5) * 100}%`,
                      backgroundColor: getStrengthColor()
                    }}
                  />
                </div>
                <div className="mt-3 space-y-1">
                  <p className="text-xs text-[var(--color-muted-foreground)] flex items-center gap-2">
                    <Icon
                      name={formData?.password?.length >= 8 ? 'CheckCircle' : 'Circle'}
                      size={14}
                      color={formData?.password?.length >= 8 ? 'var(--color-success)' : 'var(--color-muted-foreground)'}
                    />
                    At least 8 characters
                  </p>
                  <p className="text-xs text-[var(--color-muted-foreground)] flex items-center gap-2">
                    <Icon
                      name={/[A-Z]/?.test(formData?.password) ? 'CheckCircle' : 'Circle'}
                      size={14}
                      color={/[A-Z]/?.test(formData?.password) ? 'var(--color-success)' : 'var(--color-muted-foreground)'}
                    />
                    One uppercase letter
                  </p>
                  <p className="text-xs text-[var(--color-muted-foreground)] flex items-center gap-2">
                    <Icon
                      name={/[a-z]/?.test(formData?.password) ? 'CheckCircle' : 'Circle'}
                      size={14}
                      color={/[a-z]/?.test(formData?.password) ? 'var(--color-success)' : 'var(--color-muted-foreground)'}
                    />
                    One lowercase letter
                  </p>
                  <p className="text-xs text-[var(--color-muted-foreground)] flex items-center gap-2">
                    <Icon
                      name={/\d/?.test(formData?.password) ? 'CheckCircle' : 'Circle'}
                      size={14}
                      color={/\d/?.test(formData?.password) ? 'var(--color-success)' : 'var(--color-muted-foreground)'}
                    />
                    One number
                  </p>
                  <p className="text-xs text-[var(--color-muted-foreground)] flex items-center gap-2">
                    <Icon
                      name={/[^a-zA-Z0-9]/?.test(formData?.password) ? 'CheckCircle' : 'Circle'}
                      size={14}
                      color={/[^a-zA-Z0-9]/?.test(formData?.password) ? 'var(--color-success)' : 'var(--color-muted-foreground)'}
                    />
                    One special character
                  </p>
                </div>
              </div>
            )}
          </div>

          <div className="relative">
            <Input
              label="Confirm Password"
              type={showConfirmPassword ? 'text' : 'password'}
              placeholder="Re-enter your password"
              value={formData?.confirmPassword || ''}
              onChange={(e) => {
                onUpdate({ ...formData, confirmPassword: e?.target?.value });
                if (errors?.confirmPassword) {
                  setErrors({ ...errors, confirmPassword: '' });
                }
              }}
              error={errors?.confirmPassword}
              required
            />
            <button
              type="button"
              onClick={() => setShowConfirmPassword(!showConfirmPassword)}
              className="absolute right-3 top-9 text-[var(--color-muted-foreground)] hover:text-[var(--color-foreground)]"
            >
              <Icon name={showConfirmPassword ? 'EyeOff' : 'Eye'} size={18} />
            </button>
          </div>
        </div>
      </div>
      <div className="bg-[var(--color-card)] rounded-lg p-4 md:p-6 lg:p-8 border border-[var(--color-border)]">
        <div className="flex items-start gap-3 mb-4 md:mb-5 lg:mb-6">
          <div className="w-10 h-10 md:w-12 md:h-12 rounded-lg bg-[var(--color-secondary)]/10 flex items-center justify-center flex-shrink-0">
            <Icon name="HelpCircle" size={20} color="var(--color-secondary)" />
          </div>
          <div>
            <h3 className="text-lg md:text-xl lg:text-2xl font-semibold text-[var(--color-foreground)]">
              Security Question
            </h3>
            <p className="text-sm md:text-base text-[var(--color-muted-foreground)] mt-1">
              Used for account recovery if you forget your password
            </p>
          </div>
        </div>

        <div className="space-y-4 md:space-y-5 lg:space-y-6">
          <div>
            <label className="block text-sm md:text-base font-medium text-[var(--color-foreground)] mb-2">
              Select a Security Question
            </label>
            <select
              value={formData?.securityQuestion || ''}
              onChange={(e) => {
                onUpdate({ ...formData, securityQuestion: e?.target?.value });
                if (errors?.securityQuestion) {
                  setErrors({ ...errors, securityQuestion: '' });
                }
              }}
              className="w-full px-4 py-3 rounded-lg border border-[var(--color-border)] bg-[var(--color-card)] text-[var(--color-foreground)] focus:outline-none focus:ring-2 focus:ring-[var(--color-primary)]"
            >
              <option value="">Choose a question...</option>
              {securityQuestions?.map((question, index) => (
                <option key={index} value={question}>
                  {question}
                </option>
              ))}
            </select>
            {errors?.securityQuestion && (
              <p className="text-xs md:text-sm text-[var(--color-error)] flex items-center gap-1 mt-1">
                <Icon name="AlertCircle" size={14} />
                {errors?.securityQuestion}
              </p>
            )}
          </div>

          <Input
            label="Your Answer"
            type="text"
            placeholder="Enter your answer"
            value={formData?.securityAnswer || ''}
            onChange={(e) => {
              onUpdate({ ...formData, securityAnswer: e?.target?.value });
              if (errors?.securityAnswer) {
                setErrors({ ...errors, securityAnswer: '' });
              }
            }}
            error={errors?.securityAnswer}
            description="Make sure you remember this answer"
            required
          />
        </div>
      </div>
      <div className="bg-[var(--color-card)] rounded-lg p-4 md:p-6 lg:p-8 border border-[var(--color-border)]">
        <div className="flex items-start gap-3 mb-4 md:mb-5 lg:mb-6">
          <div className="w-10 h-10 md:w-12 md:h-12 rounded-lg bg-[var(--color-accent)]/10 flex items-center justify-center flex-shrink-0">
            <Icon name="Smartphone" size={20} color="var(--color-accent)" />
          </div>
          <div>
            <h3 className="text-lg md:text-xl lg:text-2xl font-semibold text-[var(--color-foreground)]">
              Two-Factor Authentication
            </h3>
            <p className="text-sm md:text-base text-[var(--color-muted-foreground)] mt-1">
              Add an extra layer of security to your account
            </p>
          </div>
        </div>

        <div className="space-y-4">
          <Checkbox
            label="Enable Two-Factor Authentication (2FA)"
            description="Receive a verification code via SMS or authenticator app when logging in"
            checked={formData?.enable2FA || false}
            onChange={(e) => {
              onUpdate({ ...formData, enable2FA: e?.target?.checked });
              if (errors?.enable2FA) {
                setErrors({ ...errors, enable2FA: '' });
              }
            }}
          />
          {errors?.enable2FA && (
            <p className="text-xs md:text-sm text-[var(--color-error)] flex items-center gap-1">
              <Icon name="AlertCircle" size={14} />
              {errors?.enable2FA}
            </p>
          )}

          <div className="bg-[var(--color-success)]/10 border border-[var(--color-success)]/20 rounded-lg p-4">
            <div className="flex gap-3">
              <Icon name="Shield" size={20} color="var(--color-success)" className="flex-shrink-0 mt-0.5" />
              <div className="text-xs md:text-sm text-[var(--color-foreground)]">
                <p className="font-medium mb-1">Recommended for maximum security</p>
                <p className="text-[var(--color-muted-foreground)]">
                  2FA significantly reduces the risk of unauthorized access to your account and cryptocurrency holdings
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div className="flex flex-col sm:flex-row gap-3 pt-4">
        <Button
          type="button"
          variant="outline"
          size="lg"
          iconName="ArrowLeft"
          iconPosition="left"
          onClick={onBack}
          className="sm:w-auto"
        >
          Back
        </Button>
        <Button
          type="submit"
          variant="default"
          size="lg"
          iconName="ArrowRight"
          iconPosition="right"
          className="sm:flex-1"
        >
          Continue to Review
        </Button>
      </div>
    </form>
  );
};

export default SecuritySetupStep;